import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Avatar } from './Avatar';
import { roleLabel } from '@/components/wom/theme';
import { Search, SlidersHorizontal, Users as UsersIcon, ExternalLink, Info } from 'lucide-react';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';

const nativeSelect = 'rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-600 bg-white';

// ROUT Access & Assignments — ROUT manages only its operational settings for
// EXISTING central users. Identity, credentials, status, global role and
// Hub-wide capabilities are owned by the TFS Administration Hub (/tfs-admin).
const UserDirectory = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [users, setUsers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState('');
  const [typeF, setTypeF] = useState('All');
  const [statusF, setStatusF] = useState('All');
  const [assignUser, setAssignUser] = useState(null);

  const loadUsers = useCallback(async () => {
    const params = {};
    if (typeF !== 'All') params.user_type = typeF;
    if (statusF !== 'All') params.status = statusF;
    if (search) params.search = search;
    const r = await axios.get(`${API}/admin/users`, { ...authHeaders, params });
    setUsers(r.data || []);
  }, [typeF, statusF, search, token]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => {
    axios.get(`${API}/admin/markets`, authHeaders).then((r) => setMarkets(r.data || [])).catch(() => {});
    axios.get(`${API}/customers`, authHeaders).then((r) => setCustomers(r.data || [])).catch(() => {});
    axios.get(`${API}/service-locations`, authHeaders).then((r) => setLocations(r.data || [])).catch(() => {});
  }, []);

  const marketNames = (ids) => (ids || []).map((id) => markets.find((m) => m.id === id)?.name).filter(Boolean);

  return (
    <div data-testid="user-directory">
      <div className="mb-3 flex items-start gap-2 rounded-lg border border-[#0E2A47]/15 bg-[#0E2A47]/[0.03] px-4 py-2.5 text-sm text-slate-600" data-testid="rout-assignments-notice">
        <Info size={15} className="mt-0.5 text-[#0E2A47]" />
        <span>
          This is <strong>ROUT Access &amp; Assignments</strong>. You manage ROUT operational settings (user type, customer, locations, markets, supervisor, job) for existing people here.
          Names, emails, login credentials, account status, roles and workspace access are managed centrally in the{' '}
          <button onClick={() => navigate('/tfs-admin?tab=capabilities')} className="font-semibold text-[#0E2A47] underline" data-testid="goto-admin-hub-inline">TFS Administration Hub</button>.
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, email, job title…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="user-search-input" />
        </div>
        <select className={`${nativeSelect} flex-1 min-w-[140px] sm:flex-none`} value={typeF} onChange={(e) => setTypeF(e.target.value)} data-testid="user-type-filter">
          {['All', 'Internal', 'External'].map((t) => <option key={t} value={t}>{t === 'All' ? 'All types' : t}</option>)}
        </select>
        <select className={`${nativeSelect} flex-1 min-w-[140px] sm:flex-none`} value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="user-status-filter">
          {['All', 'Active', 'Inactive'].map((s) => <option key={s} value={s}>{s === 'All' ? 'All statuses' : s}</option>)}
        </select>
        <button onClick={() => navigate('/tfs-admin?tab=capabilities')} data-testid="goto-admin-hub-btn"
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-[#0E2A47] px-4 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-[#0E2A47]/5 sm:w-auto">
          <ExternalLink size={15} /> Add User in TFS Admin Hub
        </button>
      </div>

      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <Table data-testid="users-table" containerClassName="max-h-[70vh] overflow-auto rounded-md">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="sticky top-0 z-10 bg-gray-50">User</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden md:table-cell">Role</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden lg:table-cell">Type</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 hidden lg:table-cell">Markets</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50">Status</TableHead>
                <TableHead className="sticky top-0 z-10 bg-gray-50 w-10 text-right">Assignments</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar userId={u.id} hasPhoto={u.has_photo ?? !!u.photo_url} name={u.full_name || u.name} size={36} />
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{u.full_name || u.name}</div>
                        <div className="text-xs text-slate-400 truncate">{u.email}</div>
                        {['client_facilities_director', 'client_location_requestor'].includes(u.role) && (() => {
                          const cName = customers.find((c) => c.id === u.customer_id)?.name || '—';
                          const assigned = (u.assigned_location_ids || []).length;
                          const custTotal = locations.filter((l) => l.customer_id === u.customer_id).length;
                          const locCount = assigned > 0 ? assigned : (u.role === 'client_facilities_director' ? custTotal : 0);
                          return (
                            <div className="text-[11px] text-slate-500 leading-tight" data-testid={`user-customer-info-${u.id}`}>
                              <span>Customer: {cName}</span>
                              <span className="mx-1 text-slate-300">·</span>
                              <span>Locations: {u.role === 'client_facilities_director' && assigned === 0 ? `All (${custTotal})` : locCount}</span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-slate-600">{roleLabel(u.role)}</TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge variant="outline" className="text-[10px]">{u.user_type || (['client_facilities_director', 'client_location_requestor'].includes(u.role) ? 'External' : 'Internal')}</Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-xs text-slate-500 max-w-[180px] truncate">
                    {marketNames(u.service_footprint).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    <span data-testid={`user-status-${u.id}`}
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.status === 'Inactive' ? 'bg-slate-200 text-slate-500' : 'bg-green-100 text-green-700'}`}>
                      {u.status || 'Active'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => setAssignUser(u)} data-testid={`user-assignments-${u.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                      <SlidersHorizontal size={13} /> Edit Assignments
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {users.length === 0 && (
            <div className="py-16 text-center text-slate-400 text-sm">
              <UsersIcon size={32} className="mx-auto mb-2 text-slate-300" /> No users match.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile / tablet: compact user cards — avatar, identity, role/type/markets, status, actions */}
      <div className="lg:hidden">
        <MobileRecordList testid="users-mobile-list">
          {users.map((u) => {
            const isClient = ['client_facilities_director', 'client_location_requestor'].includes(u.role);
            let clientInfo = null;
            if (isClient) {
              const cName = customers.find((c) => c.id === u.customer_id)?.name || '—';
              const assigned = (u.assigned_location_ids || []).length;
              const custTotal = locations.filter((l) => l.customer_id === u.customer_id).length;
              const locCount = assigned > 0 ? assigned : (u.role === 'client_facilities_director' ? custTotal : 0);
              clientInfo = `${cName} · Locations: ${u.role === 'client_facilities_director' && assigned === 0 ? `All (${custTotal})` : locCount}`;
            }
            return (
              <MobileRecordCard
                key={u.id}
                testid={`user-card-${u.id}`}
                image={<Avatar userId={u.id} hasPhoto={u.has_photo ?? !!u.photo_url} name={u.full_name || u.name} size={40} />}
                title={u.full_name || u.name}
                badge={<span data-testid={`user-status-m-${u.id}`} className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.status === 'Inactive' ? 'bg-slate-200 text-slate-500' : 'bg-green-100 text-green-700'}`}>{u.status || 'Active'}</span>}
                meta={[
                  { label: 'Email', value: u.email, full: true },
                  { label: 'Role', value: roleLabel(u.role) },
                  { label: 'Type', value: u.user_type || (isClient ? 'External' : 'Internal') },
                  { label: 'Markets', value: marketNames(u.service_footprint).join(', ') || '—', full: true },
                  ...(clientInfo ? [{ label: 'Customer', value: clientInfo, full: true }] : []),
                ]}
                actions={(
                  <button onClick={() => setAssignUser(u)} data-testid={`user-assignments-m-${u.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-[#0E2A47] hover:bg-slate-50">
                    <SlidersHorizontal size={13} /> Edit
                  </button>
                )}
              />
            );
          })}
        </MobileRecordList>
        {users.length === 0 && (
          <div className="py-16 text-center text-slate-400 text-sm">
            <UsersIcon size={32} className="mx-auto mb-2 text-slate-300" /> No users match.
          </div>
        )}
      </div>

      {assignUser && (
        <RoutAssignmentsDialog
          user={assignUser} markets={markets} customers={customers} locations={locations} allUsers={users}
          onClose={() => setAssignUser(null)} onSaved={() => { setAssignUser(null); loadUsers(); }} />
      )}
    </div>
  );
};

// ROUT-only assignment editor. Sends ONLY ROUT-specific fields to the central
// record — never identity, credentials, status, global role, or capabilities.
const RoutAssignmentsDialog = ({ user, markets, customers, locations, allUsers, onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [form, setForm] = useState({
    user_type: user.user_type || (['client_facilities_director', 'client_location_requestor'].includes(user.role) ? 'External' : 'Internal'),
    customer_id: user.customer_id || '',
    assigned_location_ids: user.assigned_location_ids || [],
    service_footprint: user.service_footprint || [],
    supervisor_id: user.supervisor_id || '',
    job_title: user.job_title || '',
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, id) => setForm((f) => ({ ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id] }));

  const custLocations = locations.filter((l) => !form.customer_id || l.customer_id === form.customer_id);
  const supervisors = (allUsers || []).filter((u) => u.id !== user.id && (u.user_type || 'Internal') === 'Internal');

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        user_type: form.user_type,
        customer_id: form.customer_id || null,
        assigned_location_ids: form.assigned_location_ids,
        service_footprint: form.service_footprint,
        supervisor_id: form.supervisor_id || null,
        job_title: form.job_title || null,
      };
      await axios.put(`${API}/admin/users/${user.id}`, payload, authHeaders);
      toast.success(`ROUT assignments updated for ${user.full_name || user.name}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save assignments');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rout-assignments-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47]">ROUT Access &amp; Assignments — {user.full_name || user.name}</DialogTitle>
        </DialogHeader>
        <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Identity, credentials, status, role and workspace access are managed in the TFS Administration Hub and are shown read-only here.
        </div>
        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-slate-500">User Type</label>
            <select className={`${nativeSelect} w-full mt-1`} value={form.user_type}
              onChange={(e) => set('user_type', e.target.value)} data-testid="assign-user-type">
              {['Internal', 'External'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Customer Assignment</label>
            <select className={`${nativeSelect} w-full mt-1`} value={form.customer_id}
              onChange={(e) => { set('customer_id', e.target.value); set('assigned_location_ids', []); }} data-testid="assign-customer">
              <option value="">— None —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Supervisor</label>
            <select className={`${nativeSelect} w-full mt-1`} value={form.supervisor_id}
              onChange={(e) => set('supervisor_id', e.target.value)} data-testid="assign-supervisor">
              <option value="">— None —</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.full_name || s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Operational Job / Subtype</label>
            <Input className="mt-1" value={form.job_title} onChange={(e) => set('job_title', e.target.value)}
              placeholder="e.g. Field Technician" data-testid="assign-job-title" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-500">Service Markets</label>
            <div className="mt-1 grid grid-cols-2 gap-1.5 max-h-32 overflow-auto rounded-md border border-gray-200 p-2 sm:grid-cols-3" data-testid="assign-markets">
              {markets.length === 0 && <span className="text-xs text-slate-400">No markets defined.</span>}
              {markets.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.service_footprint.includes(m.id)} onChange={() => toggle('service_footprint', m.id)} />
                  {m.name}
                </label>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-500">Location Assignment{form.customer_id ? '' : ' (select a customer to filter)'}</label>
            <div className="mt-1 grid grid-cols-1 gap-1.5 max-h-40 overflow-auto rounded-md border border-gray-200 p-2 sm:grid-cols-2" data-testid="assign-locations">
              {custLocations.length === 0 && <span className="text-xs text-slate-400">No locations available.</span>}
              {custLocations.map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.assigned_location_ids.includes(l.id)} onChange={() => toggle('assigned_location_ids', l.id)} />
                  <span className="truncate">{l.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-slate-600" data-testid="assign-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="assign-save">
            {busy ? 'Saving…' : 'Save Assignments'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserDirectory;
